import { TemplateFolderSelectModal } from "@/pages/talent/templates/TemplateFolderSelectModal.tsx";
import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import { EmployeeAvatarWithTooltip } from "@/components/employee/EmployeeAvatarWithTooltip.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import {
  Avatar,
  ConfirmationModal,
  EmptyStateBase,
  LinkTo,
  Page,
  PaginationBlock,
  useModals,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
  Table,
  TableCell,
} from "@skailer-com/uikit";
import { Search } from "@/components/search/Search.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import {
  PerformanceReviewTemplateEditPageStep,
  useTemplateEditPage,
} from "@/pages/talent/performance-reviews/templates/edit/performance-review-template-edit.tsx";
import {
  usePerformanceReviewTemplateDelete,
  usePerformanceReviewTemplateMove,
  usePerformanceReviewTemplateDuplicate,
  usePerformanceReviewTemplateSave,
  usePerformanceReviewTemplateSearch,
} from "@/queries/performance-reviews/use-performance-review-template.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { AnalyticReportTypes } from "@/types/analytics/analytics.types.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import {
  ApiFormTemplate,
  ApiFormTemplateFilter,
  ApiFormTemplateType,
} from "@/types/forms/form-template.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { ColumnDef, Row } from "@tanstack/react-table";
import { round } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useDebounce, useEffectOnce } from "react-use";

export const PerformanceReviewTemplatesTable = ({ folderId }: { folderId?: number }) => {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { navigateToStep } = useTemplateEditPage();
  const { canIEdit, isFeatureEnabled } = usePermissions();

  const [searchParams, setSearchParams] = useSearchParams();
  const library = filtersLibrary();
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") ?? "");

  const canManageTemplates = canIEdit(ApiPermission.performance_review_templates);
  const canDuplicateToSurveys =
    isFeatureEnabled(ApiFeatureType.talent_surveys) &&
    canIEdit(ApiPermission.surveys_module) &&
    canIEdit(ApiPermission.survey_templates);
  const templateSave = usePerformanceReviewTemplateSave();
  const templateDuplicate = usePerformanceReviewTemplateDuplicate();
  const templateDelete = usePerformanceReviewTemplateDelete();
  const templateMove = usePerformanceReviewTemplateMove();

  const {
    filter: { filter: filter },
  } = useAppFilter<ApiFormTemplateFilter>({
    filters: [library.page_number, library.text],
    source: AnalyticReportTypes.formTemplate,
  });

  const {
    data: templates,
    isLoading,
    isFetching,
  } = usePerformanceReviewTemplateSearch({ ...filter, folder_id: folderId });

  useDebounce(
    () => {
      setSearchParams((searchParams) => {
        if ((searchParams.get("search") ?? "") !== searchTerm) searchParams.delete("page");
        if (searchTerm) searchParams.set("search", searchTerm);
        else searchParams.delete("search");
        return searchParams;
      });
    },
    300,
    [searchTerm]
  );

  const handleCreate = useCallback(async () => {
    try {
      const template = await templateSave.mutateAsync({
        id: undefined,
        payload: {
          title: "",
          folder_id: folderId,
        },
      });

      analyticsService.trackEvent(analyticEvents.performanceReviewsTemplates.created, {
        [analyticProperties.id]: template.id,
      });

      navigateToStep(template.id, PerformanceReviewTemplateEditPageStep.details);
    } catch {
      return;
    }
  }, [folderId, navigateToStep, templateSave]);

  const handleDuplicate = useCallback(
    (template: ApiFormTemplate, targetType?: ApiFormTemplateType) => {
      const duplicate = async (folderId?: number) => {
        await templateDuplicate.mutateAsync({
          id: template.id,
          payload: targetType ? { target_type: targetType, folder_id: folderId } : undefined,
        });
        analyticsService.trackEvent(analyticEvents.performanceReviewsTemplates.duplicated, {
          [analyticProperties.id]: template.id,
        });
      };

      if (targetType) {
        openModal(TemplateFolderSelectModal, {
          module: "surveys",
          modalTitle: t("surveys:duplicate_template_in_surveys"),
          text: t("surveys:duplicate_template_in_surveys_confirm"),
          acceptTitle: t("common:duplicate"),
          onConfirm: duplicate,
        });
        return;
      }

      openModal(ConfirmationModal, {
        question: t("surveys:duplicate_template"),
        text: t("surveys:duplicate_template_confirm"),
        acceptTitle: t("common:duplicate"),
        onAccept: async () => {
          try {
            await duplicate();
          } catch {
            return;
          }
        },
      });
    },
    [openModal, t, templateDuplicate]
  );

  const handleDelete = useCallback(
    (template: ApiFormTemplate) => {
      openModal(ConfirmationModal, {
        question: t("surveys:delete_template"),
        text: t("common:delete_confirm"),
        acceptTitle: t("surveys:delete_template"),
        onAccept: async function () {
          try {
            await templateDelete.mutateAsync({ id: template.id });

            analyticsService.trackEvent(analyticEvents.performanceReviewsTemplates.deleted, {
              [analyticProperties.id]: template.id,
            });
          } catch {
            return;
          }
        },
      });
    },
    [openModal, t, templateDelete]
  );

  const renderTemplateCell = (tpl: ApiFormTemplate) => {
    return (
      <Stack
        direction={"horizontal"}
        items={"center"}
        gap={"smd"}
        className={"overflow-hidden text-ellipsis"}
      >
        <Avatar size={"md"} icon={<FontAwesomeIcon icon="fa-light fa-gauge" />}></Avatar>
        <TableCell>{tpl.title}</TableCell>
      </Stack>
    );
  };

  const columns = useMemo<ColumnDef<ApiFormTemplate>[]>(
    () => [
      {
        header: t("common:title"),
        accessorKey: "title",
        ...ColumnSizes.employee,
        meta: { disableRowClick: true },
        cell: (row) => (
          <LinkTo
            to={`/templates/performance-reviews/${row.row.original.id}/setup/${PerformanceReviewTemplateEditPageStep.details}`}
            variant={"secondary"}
          >
            {renderTemplateCell(row.row.original)}
          </LinkTo>
        ),
      },
      {
        header: t("common:questions"),
        accessorKey: "questions",
        ...ColumnSizes.status,
        cell: (row) => (
          <TableCell variant={"secondary"}>{row.row.original.total_questions}</TableCell>
        ),
      },
      {
        header: t("common:time_to_complete"),
        accessorKey: "time_to_complete",
        ...ColumnSizes.status,
        cell: (row) => (
          <TableCell variant={"secondary"}>
            {t("plurals:plural_minutes", {
              count: round(row.row.original.total_estimated_time_seconds / 60),
            })}
          </TableCell>
        ),
      },

      {
        header: t("common:owner"),
        cell: (row) => (
          <EmployeeAvatarWithTooltip size={"sm"} employee={row.row.original.author} enableLink />
        ),
        accessorKey: "owner",
        ...ColumnSizes.narrow,
        meta: { disableRowClick: true },
      },
    ],
    [templates]
  );

  const renderRowAction = useCallback(
    (row: Row<ApiFormTemplate>) => {
      if (!canManageTemplates) return null;
      const template = row.original;
      return (
        <DropdownMenu>
          <DropdownMenuItem
            to={`/templates/performance-reviews/${template.id}/setup/${PerformanceReviewTemplateEditPageStep.details}`}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-pen" />}
            title={t("common:edit")}
          />
          <DropdownMenuItem
            leftIcon={<FontAwesomeIcon icon="fa-light fa-copy" />}
            onClick={() => handleDuplicate(template)}
            title={t("surveys:duplicate_template") + "..."}
          />
          {canDuplicateToSurveys && (
            <DropdownMenuItem
              leftIcon={<FontAwesomeIcon icon="fa-light fa-copy" />}
              onClick={() => handleDuplicate(template, ApiFormTemplateType.survey)}
              title={`${t("surveys:duplicate_template_in_surveys")}...`}
            />
          )}
          <DropdownMenuItem
            title={t("common:move_to") + "..."}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-folder-arrow-up" />}
            onClick={() =>
              openModal(TemplateFolderSelectModal, {
                module: "performance-reviews",
                modalTitle: t("common:move_to"),
                acceptTitle: t("common:save"),
                currentFolderId: template.folder?.id,
                onConfirm: async (folderId) => {
                  await templateMove.mutateAsync({
                    id: template.id,
                    payload: { folder_id: folderId },
                  });
                },
              })
            }
          />
          <DropdownMenuItem
            leftIcon={<FontAwesomeIcon icon="fa-light fa-trash" />}
            onClick={() => handleDelete(template)}
            variant={"menuDanger"}
            title={t("common:delete") + "..."}
          />
        </DropdownMenu>
      );
    },
    [
      canManageTemplates,
      canDuplicateToSurveys,
      handleDuplicate,
      handleDelete,
      openModal,
      templateMove,
      t,
    ]
  );

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.performanceReviewsTemplates.viewed);
  });

  return (
    <Page.StickyLayout
      toolbar={
        <Stack direction={"horizontal"}>
          <Search
            value={searchTerm}
            onInput={(e) => {
              setSearchTerm(e.currentTarget.value);
            }}
            type={"text"}
            placeholder={t("common:template")}
            className={"w-96"}
          />
        </Stack>
      }
      footer={
        <PaginationBlock
          metadata={templates?.metadata}
          onPageChange={(page) => {
            setSearchParams((searchParams) => {
              searchParams.set("page", page.toString());
              return searchParams;
            });
          }}
        />
      }
    >
      <Table
        stickyHeader={true}
        renderRowAction={renderRowAction}
        onRowClick={(row) =>
          navigateToStep(row.original.id, PerformanceReviewTemplateEditPageStep.details)
        }
        data={templates?.items || []}
        columns={columns}
        showSkeleton={isLoading}
        isFetching={isFetching}
        showNotFoundError={filter.text != undefined && templates?.metadata.total_items == 0}
        notFoundComponent={<EmptyStateNothingFound />}
        emptyStateComponent={
          <EmptyStateBase
            emoji={emojiDefaults.performanceReviewTemplate.default}
            title={t("performance_review:empty_state_performance_review_templates_title")}
            description={t(
              "performance_review:empty_state_performance_review_templates_description"
            )}
            primaryButton={
              canManageTemplates && (
                <Button
                  onClick={() => handleCreate()}
                  size={"sm"}
                  variant={"secondary"}
                  leftIcon={<FontAwesomeIcon icon={"fa-light fa-plus"} />}
                >
                  {t("surveys:create_template")}
                </Button>
              )
            }
          />
        }
      />
    </Page.StickyLayout>
  );
};
